<template>
  <AppLayout>
    <div class="users-page">
      <div class="page-header">
        <h1>Utilisateurs</h1>
        <BaseButton
          v-if="activeTab === 'users'"
          variant="primary"
          @click="showCreateModal = true"
        >
          + Nouvel utilisateur
        </BaseButton>
      </div>

      <!-- Tab bar -->
      <div class="tab-bar">
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === 'users' }"
          @click="activeTab = 'users'"
        >
          Utilisateurs
        </button>
        <button
          v-if="currentUser?.role === 'ADMIN'"
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === 'activite' }"
          @click="switchToActivite"
        >
          Activité
        </button>
      </div>

      <!-- Tab: Utilisateurs -->
      <div v-if="activeTab === 'users'">
        <div v-if="error" class="error">{{ error }}</div>
        <BaseTable
          :columns="columns"
          :items="users"
          :loading="loading"
          :show-actions="true"
          @view="viewUser"
        >
          <template #cell-role="{ value }">
            <BaseBadge :variant="getRoleVariant(value)">{{ value }}</BaseBadge>
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="viewUser(item)">
              Voir
            </BaseButton>
            <BaseButton variant="ghost" size="sm" @click.stop="editUser(item)">
              Modifier
            </BaseButton>
            <BaseButton
              variant="ghost"
              size="sm"
              @click.stop="confirmDelete(item)"
              :disabled="item.id === currentUserId"
            >
              Supprimer
            </BaseButton>
          </template>
        </BaseTable>

        <div v-if="!loading && users.length > 0" class="pagination">
          <BaseButton variant="ghost" @click="previousPage" :disabled="offset === 0">
            ← Précédent
          </BaseButton>
          <span class="pagination-info">
            Page {{ currentPage }} sur {{ totalPages }} ({{ total }} utilisateurs)
          </span>
          <BaseButton variant="ghost" @click="nextPage" :disabled="offset + limit >= total">
            Suivant →
          </BaseButton>
        </div>
      </div>

      <!-- Tab: Activité -->
      <div v-if="activeTab === 'activite'">
        <!-- Filter bar -->
        <div class="journal-filters">
          <BaseInput
            v-model.trim="journalFilters.email"
            placeholder="Email"
            label=""
          />
          <select v-model="journalFilters.typeAction" class="filter-select">
            <option value="">Toutes les actions</option>
            <option value="LOGIN">LOGIN</option>
            <option value="CREATE">CREATE</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
          </select>
          <input
            v-model="journalFilters.dateDebut"
            type="date"
            class="filter-date"
            placeholder="Date début"
          />
          <input
            v-model="journalFilters.dateFin"
            type="date"
            class="filter-date"
            placeholder="Date fin"
          />
          <BaseButton variant="primary" @click="applyJournalFilters">
            Filtrer
          </BaseButton>
        </div>

        <!-- Loading -->
        <div v-if="journalLoading" class="journal-loading">Chargement…</div>

        <!-- Empty state -->
        <div v-else-if="!journalLoading && journal.length === 0" class="journal-empty">
          Aucune activité trouvée
        </div>

        <!-- Table -->
        <div v-else class="journal-table-wrapper">
          <table class="journal-table">
            <thead>
              <tr>
                <th>Date/Heure</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Action</th>
                <th>Ressource</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in journal" :key="entry.id">
                <td>{{ formatDateTime(entry.cree_le) }}</td>
                <td>{{ entry.email }}</td>
                <td>{{ entry.role }}</td>
                <td>
                  <BaseBadge :variant="getActionVariant(entry.type_action)">
                    {{ entry.type_action }}
                  </BaseBadge>
                </td>
                <td>{{ entry.ressource ?? '—' }}<span v-if="entry.ressource_id"> #{{ entry.ressource_id }}</span></td>
                <td>{{ entry.ip ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="!journalLoading && journal.length > 0" class="pagination">
          <BaseButton
            variant="ghost"
            @click="journalPreviousPage"
            :disabled="journalPage === 1"
          >
            ← Précédent
          </BaseButton>
          <span class="pagination-info">Page {{ journalPage }} sur {{ journalTotalPages }}</span>
          <BaseButton
            variant="ghost"
            @click="journalNextPage"
            :disabled="journalPage >= journalTotalPages"
          >
            Suivant →
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Modal de création/édition -->
    <FormPage v-if="showCreateModal || showEditModal" @close="closeModal">
      <template #title>
        {{ showCreateModal ? 'Nouvel utilisateur' : 'Modifier utilisateur' }}
      </template>
      <form @submit.prevent="handleSubmit" class="user-form">
        <BaseInput
          v-model.trim="userForm.nom"
          label="Nom"
          placeholder="Nom complet"
          required
        />
        <BaseInput
          v-model.trim="userForm.email"
          label="Email"
          type="email"
          placeholder="email@example.com"
          required
        />
        <BaseSelect
          v-model="userForm.role"
          label="Rôle"
          required
        >
          <option value="ADMIN">ADMIN</option>
          <option value="BUREAU">BUREAU</option>
          <option value="USINE">USINE</option>
          <option value="CHANTIER">CHANTIER</option>
          <option value="SUPERVISION">SUPERVISION</option>
        </BaseSelect>
        <BaseInput
          v-if="showCreateModal"
          v-model.trim="userForm.password"
          label="Mot de passe"
          type="password"
          placeholder="••••••••"
          required
        />
        <div v-if="formError" class="error-message">{{ formError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="submitting">
            {{ showCreateModal ? 'Créer' : 'Enregistrer' }}
          </BaseButton>
        </div>
      </form>
    </FormPage>

    <!-- Modal de confirmation de suppression -->
    <FormPage v-if="showDeleteModal" @close="showDeleteModal = false">
      <template #title>Confirmer la suppression</template>
      <p>Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{{ userToDelete?.nom }}</strong> ?</p>
      <div class="form-actions">
        <BaseButton type="button" variant="ghost" @click="showDeleteModal = false">Annuler</BaseButton>
        <BaseButton variant="primary" @click="handleDelete" :loading="deleting">
          Supprimer
        </BaseButton>
      </div>
    </FormPage>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseBadge from '../components/BaseBadge.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import FormPage from '../components/FormPage.vue';
import { apiFetch } from '../utils/api';
import { useAuth } from '../composables/useAuth';

interface User {
  id: number;
  nom: string;
  email: string;
  role: string;
  creeLe: string;
}

// API returns snake_case directly from the DB rows
interface JournalEntry {
  id: number;
  utilisateur_id: number | null;
  email: string;
  role: string;
  type_action: 'LOGIN' | 'CREATE' | 'UPDATE' | 'DELETE';
  ressource: string | null;
  ressource_id: number | null;
  ip: string | null;
  cree_le: string;
}

const router = useRouter();
const { user: currentUser } = useAuth();
const currentUserId = computed(() => currentUser.value?.id);

// Tab state
const activeTab = ref<'users' | 'activite'>('users');

// Users state
const users = ref<User[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const limit = ref(20);
const offset = ref(0);
const total = ref(0);

const showCreateModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const submitting = ref(false);
const deleting = ref(false);
const formError = ref<string | null>(null);
const userToDelete = ref<User | null>(null);
const editingUserId = ref<number | null>(null);

const userForm = reactive({
  nom: '',
  email: '',
  role: 'SUPERVISION',
  password: ''
});

// Journal state
const journal = ref<JournalEntry[]>([]);
const journalTotal = ref(0);
const journalPage = ref(1);
const journalLoading = ref(false);
const journalFilters = reactive({ email: '', typeAction: '', dateDebut: '', dateFin: '' });

const journalTotalPages = computed(() => Math.max(1, Math.ceil(journalTotal.value / 50)));

const columns: TableColumn[] = [
  { key: 'nom', label: 'Nom' },
  { key: 'email', label: 'Email' },
  { key: 'role', label: 'Rôle' },
  {
    key: 'creeLe',
    label: 'Créé le',
    formatter: (value: string) => new Date(value).toLocaleDateString('fr-FR')
  }
];

const currentPage = computed(() => Math.floor(offset.value / limit.value) + 1);
const totalPages = computed(() => Math.ceil(total.value / limit.value));

onMounted(() => {
  loadUsers();
});

async function loadUsers() {
  loading.value = true;
  error.value = null;
  try {
    const response = await apiFetch(`/authentification/users?limit=${limit.value}&offset=${offset.value}`);
    const data = response as { items: User[]; total: number; limit: number; offset: number };
    users.value = data.items || [];
    total.value = data.total || 0;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement';
  } finally {
    loading.value = false;
  }
}

async function loadJournal() {
  journalLoading.value = true;
  try {
    const params = new URLSearchParams();
    params.set('page', String(journalPage.value));
    params.set('limit', '50');
    if (journalFilters.email) params.set('email', journalFilters.email);
    if (journalFilters.typeAction) params.set('typeAction', journalFilters.typeAction);
    if (journalFilters.dateDebut) params.set('dateDebut', journalFilters.dateDebut);
    if (journalFilters.dateFin) params.set('dateFin', journalFilters.dateFin);
    const res = await apiFetch(`/journal?${params}`) as { items: JournalEntry[]; total: number; page: number; limit: number };
    journal.value = res.items;
    journalTotal.value = res.total;
  } catch {
    journal.value = [];
  } finally {
    journalLoading.value = false;
  }
}

function applyJournalFilters() {
  journalPage.value = 1;
  loadJournal();
}

function switchToActivite() {
  activeTab.value = 'activite';
  if (journal.value.length === 0 && !journalLoading.value) {
    loadJournal();
  }
}

function journalNextPage() {
  if (journalPage.value < journalTotalPages.value) {
    journalPage.value++;
    loadJournal();
  }
}

function journalPreviousPage() {
  if (journalPage.value > 1) {
    journalPage.value--;
    loadJournal();
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function getActionVariant(action: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (action) {
    case 'LOGIN': return 'primary';
    case 'CREATE': return 'success';
    case 'UPDATE': return 'default';
    case 'DELETE': return 'error';
    default: return 'default';
  }
}

function viewUser(user: User) {
  router.push({ name: 'user-detail', params: { id: user.id } });
}

function editUser(user: User) {
  editingUserId.value = user.id;
  userForm.nom = user.nom;
  userForm.email = user.email;
  userForm.role = user.role;
  userForm.password = '';
  showEditModal.value = true;
}

function confirmDelete(user: User) {
  userToDelete.value = user;
  showDeleteModal.value = true;
}

function closeModal() {
  showCreateModal.value = false;
  showEditModal.value = false;
  formError.value = null;
  userForm.nom = '';
  userForm.email = '';
  userForm.role = 'SUPERVISION';
  userForm.password = '';
  editingUserId.value = null;
}

async function handleSubmit() {
  formError.value = null;
  submitting.value = true;

  try {
    if (showCreateModal.value) {
      await apiFetch('/authentification/register', {
        method: 'POST',
        body: {
          nom: userForm.nom,
          email: userForm.email,
          role: userForm.role,
          password: userForm.password
        }
      });
    } else if (editingUserId.value) {
      await apiFetch(`/authentification/users/${editingUserId.value}`, {
        method: 'PUT',
        body: {
          nom: userForm.nom,
          email: userForm.email,
          role: userForm.role
        }
      });
    }
    closeModal();
    loadUsers();
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement';
  } finally {
    submitting.value = false;
  }
}

async function handleDelete() {
  if (!userToDelete.value) return;

  deleting.value = true;
  try {
    await apiFetch(`/authentification/users/${userToDelete.value.id}`, {
      method: 'DELETE'
    });
    showDeleteModal.value = false;
    userToDelete.value = null;
    loadUsers();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors de la suppression';
  } finally {
    deleting.value = false;
  }
}

function nextPage() {
  if (offset.value + limit.value < total.value) {
    offset.value += limit.value;
    loadUsers();
  }
}

function previousPage() {
  if (offset.value > 0) {
    offset.value = Math.max(0, offset.value - limit.value);
    loadUsers();
  }
}

function getRoleVariant(role: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (role) {
    case 'ADMIN':
      return 'error';
    case 'BUREAU':
      return 'primary';
    case 'USINE':
      return 'success';
    default:
      return 'default';
  }
}
</script>

<style scoped>
.users-page {
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
  gap: var(--spacing-md);
}

.page-header h1 {
  margin: 0;
}

/* Tab bar */
.tab-bar {
  display: flex;
  gap: 0;
  border-bottom: 2px solid var(--color-border, #e5e7eb);
  margin-bottom: var(--spacing-lg);
}

.tab-btn {
  padding: 0.6rem 1.25rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium, 500);
  color: var(--color-text-secondary, #6b7280);
  transition: color 0.15s, border-color 0.15s;
}

.tab-btn:hover {
  color: var(--color-text, #111827);
}

.tab-btn--active {
  color: var(--color-primary, #2563eb);
  border-bottom-color: var(--color-primary, #2563eb);
  font-weight: var(--font-weight-semibold, 600);
}

/* Journal filters */
.journal-filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  align-items: flex-end;
  margin-bottom: var(--spacing-lg);
}

.filter-select,
.filter-date {
  height: 38px;
  padding: 0 0.75rem;
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: var(--radius-md, 6px);
  font-size: var(--font-size-sm);
  background: var(--color-surface, #fff);
  color: var(--color-text, #111827);
}

/* Journal table */
.journal-table-wrapper {
  overflow-x: auto;
}

.journal-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.journal-table th,
.journal-table td {
  padding: 0.6rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--color-border, #e5e7eb);
  white-space: nowrap;
}

.journal-table th {
  background: var(--color-surface-alt, #f9fafb);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--color-text-secondary, #6b7280);
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.journal-table tbody tr:hover {
  background: var(--color-surface-alt, #f9fafb);
}

.journal-loading,
.journal-empty {
  padding: var(--spacing-xl);
  text-align: center;
  color: var(--color-text-secondary, #6b7280);
  font-size: var(--font-size-sm);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
}

.pagination-info {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.user-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
}

.error-message {
  padding: var(--spacing-sm);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  font-size: var(--font-size-sm);
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
  margin-bottom: var(--spacing-lg);
}
</style>
